import { ComputedRef, ref, Ref } from "vue";
import useSWRV from "swrv";
import { axios, deepUndefineKeyToNull, fetcher } from "/@/modules/axios";
import { errorHandle } from "/@/modules/error";
import { News, NewsClient, NewsLabel } from "/@/types/news";

export function useNews() {
  function getNewsItemKey(id?: Ref<number>, options: { admin?: boolean } = {}) {
    const { admin } = options;

    if (!id || !id.value) {
      return null;
    }

    let uri = `/api/v1/news/${id.value}?`;

    if (admin) {
      uri += "admin=true&";
    }

    return uri;
  }

  function getNewsItem(
    id?: Ref<number> | ComputedRef<number>,
    options: { admin?: boolean; association?: boolean } = {}
  ) {
    return useSWRV<NewsClient>(() => getNewsItemKey(id, options), fetcher);
  }

  function getNewsKey(
    organizationId: Ref<number>,
    options: {
      newsTypes?: NewsLabel[];
      all?: boolean;
      top?: boolean;
      association?: boolean;
    } = {}
  ) {
    const { newsTypes, all, top, association } = options;

    let uri = "/api/v1/news?";

    if (organizationId.value) {
      uri += `organizationId=${organizationId.value}&`;
    }

    if (newsTypes) {
      uri += `newsTypes=${newsTypes.join(",")}&`;
    }

    if (association) {
      uri += `association=true&`;
    }

    if (all) {
      uri += `all=true&`;
    }

    if (top) {
      uri += `top=true`;
    }

    return uri;
  }

  function getNews(
    organizationId: Ref<number>,
    options: {
      newsTypes?: NewsLabel[];
      all?: boolean;
      top?: boolean;
      association?: boolean;
    } = {}
  ) {
    return useSWRV<NewsClient[]>(
      () => getNewsKey(organizationId, options),
      fetcher
    );
  }

  function getUsersNews(organizationId: Ref<number>) {
    function getKey() {
      if (!organizationId.value) return null;

      return `/api/v1/users/news?organizationId=${organizationId.value}`;
    }

    return useSWRV<NewsClient[]>(getKey, fetcher);
  }

  async function createNews(news: News) {
    try {
      await axios.post("/api/v1/news", {
        news,
      });
      return true;
    } catch (error) {
      errorHandle(error);
      return false;
    }
  }

  async function updateNews(id: number, news: News) {
    try {
      await axios.patch(`/api/v1/news/${id}`, {
        news: deepUndefineKeyToNull(news),
      });
      return true;
    } catch (error) {
      errorHandle(error);
      return false;
    }
  }

  async function destroyNews(id: number | number[]) {
    try {
      if (Array.isArray(id)) {
        await axios.post("/api/v1/news/bulk_destroy", {
          news: {
            ids: id,
          },
        });
        return true;
      } else {
        await axios.delete(`/api/v1/news/${id}`);
      }
      return true;
    } catch (error) {
      errorHandle(error);
      return false;
    }
  }

  // trash

  function getTrashedNewsKey(organizationId: Ref<number>) {
    if (!organizationId.value) return null;

    return `/api/v1/news/trashed_index?organizationId=${organizationId.value}`;
  }

  function getTrashedNews(organizationId: Ref<number>) {
    return useSWRV<NewsClient[]>(
      () => getTrashedNewsKey(organizationId),
      fetcher
    );
  }

  async function trashNews(id: number | number[]) {
    try {
      if (Array.isArray(id)) {
        await axios.post("/api/v1/news/bulk_trash", {
          news: {
            ids: id,
          },
        });
      } else {
        await axios.delete(`/api/v1/news/${id}/trash`);
      }
      return true;
    } catch (error) {
      errorHandle(error);
      return false;
    }
  }

  async function restoreNews(id: number | number[]) {
    try {
      if (Array.isArray(id)) {
        await axios.post("/api/v1/news/bulk_restore", {
          news: {
            ids: id,
          },
        });
        return true;
      } else {
        await axios.patch(`/api/v1/news/${id}/restore`);
      }

      return true;
    } catch (error) {
      errorHandle(error);
      return false;
    }
  }

  // filter

  const newsFilterOptions = [
    { value: "all", label: "全て" },
    { value: "everyone", label: "一般公開" },
    { value: "member", label: "会員向け" },
    { value: "nonpublished", label: "非公開" },
  ];

  const selectedNewsLabel = ref<NewsClient["label"] | "all" | null>("all");
  const selectedForMembers = ref<
    "all" | "everyone" | "member" | "nonpublished"
  >("all");

  function newsFilter(
    news: NewsClient[],
    selectedNewsLabel: NewsClient["label"] | "all" | null,
    selectedForMembers: "all" | "everyone" | "member" | "nonpublished",
    organizationId: number | undefined = undefined
  ) {
    return news.filter((n) => {
      if (organizationId && n.organizationId !== organizationId) {
        return false;
      }

      if (
        selectedNewsLabel !== "all" &&
        selectedNewsLabel &&
        selectedNewsLabel !== n.label
      ) {
        return false;
      }

      if (selectedForMembers !== "all") {
        if (selectedForMembers !== "nonpublished" && !n.published) {
          return false;
        }

        if (selectedForMembers === "nonpublished" && n.published) {
          return false;
        }

        if (selectedForMembers === "everyone" && n.forMembers) {
          return false;
        }

        if (selectedForMembers === "member" && !n.forMembers) {
          return false;
        }
      }

      return true;
    });
  }

  return {
    getNewsItem,
    getNews,
    getUsersNews,
    createNews,
    updateNews,
    destroyNews,

    // trash
    getTrashedNews,
    trashNews,
    restoreNews,

    // filter

    newsFilterOptions,
    selectedNewsLabel,
    selectedForMembers,
    newsFilter,
  };
}
